import React, { useState } from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { createDevice } from 'core/API/gameMasterUserDevices';
import { CreateDeviceRequest } from 'types';
import styles from './DeviceForm.module.scss';
import deviceStyles from './GamemasterUserDevices.module.scss';
import { Button as MuiButton, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';

interface CreateDeviceSettingsProps {
    workplaceId: number;
    devices: any[];
    handleBackClick: () => void;
    onDeviceCreated?: () => void;
}

const CreateDeviceSettings: React.FC<CreateDeviceSettingsProps> = ({
    workplaceId,
    devices,
    handleBackClick,
    onDeviceCreated,
}) => {
    const [name, setName] = useState('');
    const [identifier, setIdentifier] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    const isDuplicateName = devices.some(
        (device: any) => device.name.toLowerCase() === name.toLowerCase()
    );

    const handleCreateDevice = async () => {
        setError(null);
        setIsLoading(true);

        if (devices.length >= 5) {
            setError('Maximum of 5 devices allowed.');
            setIsLoading(false);
            return;
        }
        if (!name.trim() || !identifier.trim()) {
            setError('Please enter both device name and type.');
            setIsLoading(false);
            return;
        }
        if (isDuplicateName) {
            setError('This device name already exists.');
            setIsLoading(false);
            return;
        }

        try {
            const newDeviceData: CreateDeviceRequest = {
                name,
                identifier,
                workplace: { id: workplaceId },
            };
            await createDevice(newDeviceData);
            await queryClient.invalidateQueries(['userDevices', workplaceId]);
            onDeviceCreated?.();
            handleBackClick();
        } catch (error: any) {
            console.error('Error creating device:', error);
            setError(error?.message || 'Failed to create device. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const isFormValid = name.trim() && identifier.trim() && !isDuplicateName && devices.length < 5;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleCreateDevice();
    };

    return (
        <div className={deviceStyles.createDeviceSetting}>
            <div className={deviceStyles.formContainer}>
                <Grid container direction="column" spacing={2} pl={2}>
                    <Grid container direction="row" justifyContent="space-between">
                        <Grid>
                            <MuiButton className={deviceStyles.backButton} onClick={handleBackClick}>
                                Back
                            </MuiButton>
                        </Grid>
                        <Grid item xs={6} display={'flex'} justifyContent="flex-end">
                            <Grid>
                                <MuiButton
                                    className={deviceStyles.saveButton}
                                    onClick={handleSubmit}
                                    disabled={isLoading || !isFormValid}
                                >
                                    {isLoading ? 'Saving...' : 'Save'}
                                </MuiButton>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
                        <h2 className={deviceStyles.formTitle}>Create Device</h2>
                    </Grid>
                </Grid>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className={deviceStyles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.formRow}>
                        <div className={styles.formField}>
                            <label className={styles.label}>Device Name</label>
                            <Input
                                type="text"
                                required
                                placeholder="Device Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className={styles.input}
                            />
                            {isDuplicateName && (
                                <span className={styles.fieldError}>This device name already exists.</span>
                            )}
                        </div>

                        <div className={styles.formField}>
                            <label className={styles.label}>Device Type</label>
                            <Select value={identifier} onValueChange={setIdentifier}>
                                <SelectTrigger className={styles.select}>
                                    <SelectValue placeholder="Select Device Type" />
                                </SelectTrigger>
                                <SelectContent className={styles.selectContent}>
                                    <SelectItem className={styles.selectItem} value="ipad">iPad</SelectItem>
                                    <SelectItem className={styles.selectItem} value="mobile">Mobile</SelectItem>
                                    <SelectItem className={styles.selectItem} value="other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateDeviceSettings;

